import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, Pencil, X, Check, Trash2, Upload, Camera, Star, StarOff, Loader2, Images, Plus, Film, Link2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { resolveImageUrl } from "../utils/image";
import SEO from "../components/SEO";
import { useAdminAuth } from "../components/AdminGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ["Film", "Photography", "Social Media Content"];
const editCategories = ["Film", "Photography", "Social Media Content"];

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

// ── Cinematic Video Strip with hover preview ──
function VideoStrip({ project, index }) {
  const ytId = extractYouTubeId(project.video_url);

  return (
    <Link to={`/portfolio/${project.id}`} data-testid={`project-${project.id}`}
      className="block relative h-[220px] md:h-[300px] lg:h-[360px] overflow-hidden group"
    >
      {/* Fallback thumbnail (shows while iframe loads) */}
      <img
        src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Always-playing muted loop */}
      {ytId && (
        <div className="absolute inset-0 z-[1] overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${ytId}&start=10&end=20&vq=hd1080&disablekb=1&fs=0&iv_load_policy=3`}
            title={project.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ width: '100vw', height: '56.25vw', minHeight: '100%', minWidth: '177.77%' }}
          />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent group-hover:from-black/50 group-hover:via-black/10 transition-all duration-700 z-[2]" />
      {/* Content */}
      <div className="absolute inset-0 flex items-center z-[3]">
        <div className="max-w-7xl w-full mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}>
            <h3 className="text-[#EDEDED] text-2xl md:text-3xl lg:text-4xl font-bold mb-2 drop-shadow-lg">
              {project.title}
            </h3>
            <p className="text-[#A3A3A3] text-sm md:text-base mb-3 drop-shadow">
              {project.client || project.category}{project.client ? ` · ${project.category}` : ""}
            </p>
            <span className="inline-flex items-center gap-3 text-[#D4C5B0] text-base md:text-lg font-medium drop-shadow mt-1">
              <span className="w-10 h-10 md:w-12 md:h-12 bg-[#D4C5B0]/20 border border-[#D4C5B0]/40 rounded-full flex items-center justify-center flex-shrink-0">
                <Play size={18} fill="#D4C5B0" className="ml-0.5 text-[#D4C5B0]" />
              </span>
              Play Film
            </span>
          </motion.div>
        </div>
      </div>
    </Link>
  );
}

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState(searchParams.get("category") || "Film");
  const [editMode, setEditMode] = useState(false);
  const { authenticated: isAdmin } = useAdminAuth();
  const [editingProject, setEditingProject] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState([]);
  const [showAlbumCreator, setShowAlbumCreator] = useState(false);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [albumCoverIndex, setAlbumCoverIndex] = useState(0);
  const [albumForm, setAlbumForm] = useState({ title: "", category: "Photography", client: "", description: "", problem: "", approach: "", outcome: "", featured: false });
  const [albumSaving, setAlbumSaving] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoCategory, setVideoCategory] = useState("Film");
  const [videoClient, setVideoClient] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [videoFeatured, setVideoFeatured] = useState(false);
  const [videoSaving, setVideoSaving] = useState(false);
  const [videoThumbFile, setVideoThumbFile] = useState(null);
  const [videoThumbPreview, setVideoThumbPreview] = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`);
      setProjects(res.data);
    } catch (e) { console.error(e); }
  };

  const filtered = projects.filter(p => {
    if (activeFilter === "Film") return p.media_type === "video" && p.category === "Film";
    if (activeFilter === "Social Media Content") return p.category === "Social Media Content";
    return p.category === activeFilter;
  });
  const filteredFilms = filtered.filter(p => p.media_type === "video" && p.category === "Film");
  const filteredSocial = filtered.filter(p => p.category === "Social Media Content");
  const filteredPhotos = filtered.filter(p => p.media_type !== "video" && p.category !== "Social Media Content");

  // ── Drag & Drop for album ──
  const handleDragOver = useCallback((e) => { if (editMode) { e.preventDefault(); setIsDragging(true); } }, [editMode]);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    if (!editMode) return;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    if (files.length) {
      const newPhotos = files.map(f => ({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        file: f, preview: URL.createObjectURL(f)
      }));
      setAlbumPhotos(prev => [...prev, ...newPhotos]);
      setShowAlbumCreator(true);
    }
  }, [editMode]);

  const addAlbumFiles = (files) => {
    const images = files.filter(f => f.type.startsWith("image/"));
    if (!images.length) return;
    const newPhotos = images.map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      file: f, preview: URL.createObjectURL(f)
    }));
    setAlbumPhotos(prev => [...prev, ...newPhotos]);
    if (!showAlbumCreator) setShowAlbumCreator(true);
  };

  const removeAlbumPhoto = (id) => {
    setAlbumPhotos(prev => {
      const p = prev.find(x => x.id === id);
      if (p) URL.revokeObjectURL(p.preview);
      return prev.filter(x => x.id !== id);
    });
  };

  const saveAlbum = async () => {
    if (!albumPhotos.length) { toast.error("Add at least one photo"); return; }
    if (!albumForm.title.trim()) { toast.error("Add a title"); return; }
    setAlbumSaving(true);
    try {
      const uploadedUrls = [];
      for (const photo of albumPhotos) {
        const formData = new FormData();
        formData.append("file", photo.file);
        const res = await axios.post(`${API}/upload/image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        uploadedUrls.push(res.data.image_url);
      }
      const coverUrl = uploadedUrls[albumCoverIndex] || uploadedUrls[0];
      await axios.post(`${API}/portfolio`, {
        title: albumForm.title, category: albumForm.category,
        image_url: coverUrl, images: uploadedUrls,
        description: albumForm.description || null, client: albumForm.client || null,
        problem: albumForm.problem || null, approach: albumForm.approach || null,
        outcome: albumForm.outcome || null, featured: albumForm.featured, media_type: "photo"
      });
      toast.success("Album created!");
      albumPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      setAlbumPhotos([]); setShowAlbumCreator(false);
      setAlbumForm({ title: "", category: "Photography", client: "", description: "", problem: "", approach: "", outcome: "", featured: false });
      fetchProjects();
    } catch (e) { toast.error("Failed to save album"); }
    finally { setAlbumSaving(false); }
  };

  const addVideo = async () => {
    const ytId = extractYouTubeId(videoUrl);
    if (!ytId) { toast.error("Paste a valid YouTube link"); return; }
    setVideoSaving(true);
    try {
      let imageUrl = `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`;

      if (videoThumbFile) {
        const formData = new FormData();
        formData.append("file", videoThumbFile);
        const uploadRes = await axios.post(`${API}/upload/image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        imageUrl = uploadRes.data.image_url;
      }

      await axios.post(`${API}/portfolio`, {
        title: videoTitle || "Untitled Video",
        category: videoCategory,
        image_url: imageUrl,
        client: videoClient || null,
        description: videoDescription || null,
        featured: videoFeatured,
        media_type: "video",
        video_url: videoUrl
      });
      toast.success("Video added!");
      setVideoUrl(""); setVideoTitle(""); setVideoCategory("Film"); setVideoClient(""); setVideoDescription(""); setVideoFeatured(false);
      if (videoThumbPreview) URL.revokeObjectURL(videoThumbPreview);
      setVideoThumbFile(null); setVideoThumbPreview(null);
      fetchProjects();
    } catch (e) { toast.error("Failed to add video"); }
    finally { setVideoSaving(false); }
  };

  const deleteProject = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`);
      toast.success("Deleted");
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (e) { toast.error("Failed to delete"); }
  };

  const toggleFeatured = async (project) => {
    try {
      await axios.patch(`${API}/portfolio/${project.id}`, { featured: !project.featured });
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, featured: !p.featured } : p));
      toast.success(project.featured ? "Removed from featured" : "Added to featured");
    } catch (e) { toast.error("Failed to update"); }
  };

  return (
    <div data-testid="portfolio-page" className="pt-40 pb-16 min-h-screen bg-[#050505]"
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      <SEO
        title="Portfolio"
        description="Portfolio of commercial photography, video production and social media content by OBRA Creatives for brands in Manawatū, Palmerston North and Wellington."
        path="/portfolio"
      />

      {/* Drop overlay */}
      <AnimatePresence>
        {isDragging && editMode && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#050505]/90 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <Upload className="w-20 h-20 mx-auto mb-6 text-[#D4C5B0]" />
              <p className="text-[#EDEDED] text-2xl font-medium">Drop photos to upload</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[#D4C5B0] text-xs uppercase tracking-[0.3em] mb-4 block">Portfolio</span>
            <div className="flex items-end justify-between">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EDEDED] leading-[0.95]">
                Our Work
              </h1>
              {isAdmin && <button onClick={() => setEditMode(!editMode)} data-testid="edit-mode-toggle"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  editMode
                    ? "bg-[#D4C5B0] text-[#050505]"
                    : "border border-[#333] text-[#666] hover:text-[#EDEDED] hover:border-[#555]"
                }`}>
                <Pencil size={15} />
                {editMode ? "Done Editing" : "Edit"}
              </button>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Album Creator (edit mode) */}
      <AnimatePresence>
        {editMode && (
          <motion.section initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8">
              {!showAlbumCreator ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <button onClick={() => setShowAlbumCreator(true)} data-testid="create-album-btn"
                    className="border-2 border-dashed border-[#333] hover:border-[#D4C5B0] rounded-xl p-8 transition-all group cursor-pointer bg-[#0A0A0A] hover:bg-[#0E0E0E]">
                    <div className="text-center">
                      <Camera className="w-10 h-10 mx-auto mb-3 text-[#555] group-hover:text-[#D4C5B0] transition-colors" />
                      <p className="text-[#EDEDED] text-lg font-medium">New Photo Album</p>
                      <p className="text-[#555] text-sm mt-1">Drop photos, add details, publish</p>
                    </div>
                  </button>
                  <div className="border border-[#1A1A1A] rounded-xl p-6 bg-[#0A0A0A]">
                    <div className="flex items-center gap-2 mb-4">
                      <Film size={18} className="text-[#D4C5B0]" />
                      <h3 className="text-[#EDEDED] font-medium">Add Video</h3>
                    </div>
                    <div className="relative mb-3">
                      <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                      <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && extractYouTubeId(videoUrl)) addVideo(); }}
                        placeholder="Paste YouTube link..." data-testid="portfolio-video-url"
                        className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] pl-9 pr-4 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                    </div>
                    {extractYouTubeId(videoUrl) && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                          <iframe src={`https://www.youtube.com/embed/${extractYouTubeId(videoUrl)}`} title="Preview"
                            className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input type="text" value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)}
                            placeholder="Title" data-testid="portfolio-video-title"
                            className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                          <select value={videoCategory} onChange={(e) => setVideoCategory(e.target.value)} data-testid="portfolio-video-category"
                            className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none">
                            <option value="Film">Film</option>
                            <option value="Social Media Content">Social Media Content</option>
                          </select>
                        </div>
                        <input type="text" value={videoClient} onChange={(e) => setVideoClient(e.target.value)}
                          placeholder="Client / Brand name" data-testid="portfolio-video-client"
                          className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                        <textarea value={videoDescription} onChange={(e) => setVideoDescription(e.target.value)}
                          placeholder="Short description" rows={2} data-testid="portfolio-video-description"
                          className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                        {/* Custom Thumbnail */}
                        <div>
                          <label className="block text-[#555] text-xs mb-1">Custom Thumbnail</label>
                          {videoThumbPreview ? (
                            <div className="relative w-32 aspect-video rounded-lg overflow-hidden bg-[#1A1A1A]">
                              <img src={videoThumbPreview} alt="Thumbnail" className="w-full h-full object-cover" />
                              <button onClick={() => { URL.revokeObjectURL(videoThumbPreview); setVideoThumbFile(null); setVideoThumbPreview(null); }}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500/80 rounded-full flex items-center justify-center text-white hover:bg-red-500">
                                <X size={10} />
                              </button>
                            </div>
                          ) : (
                            <div className="relative w-32 aspect-video border border-dashed border-[#333] hover:border-[#555] rounded-lg flex items-center justify-center cursor-pointer transition-colors bg-[#0A0A0A]">
                              <input type="file" accept="image/*"
                                onChange={(e) => { if (e.target.files[0]) { setVideoThumbFile(e.target.files[0]); setVideoThumbPreview(URL.createObjectURL(e.target.files[0])); } }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="portfolio-video-thumbnail-input" />
                              <div className="text-center">
                                <Upload size={14} className="mx-auto text-[#555] mb-0.5" />
                                <p className="text-[#555] text-[10px]">Thumbnail</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={videoFeatured} onChange={(e) => setVideoFeatured(e.target.checked)} className="w-4 h-4 accent-[#D4C5B0]" />
                            <span className="text-[#A3A3A3] text-sm">Featured</span>
                          </label>
                          <button onClick={addVideo} disabled={videoSaving} data-testid="portfolio-add-video-btn"
                            className="flex items-center gap-2 px-4 py-2 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-50 transition-colors">
                            {videoSaving ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
                            {videoSaving ? "Adding..." : "Add Video"}
                          </button>
                        </div>
                      </motion.div>
                    )}
                    {videoUrl && !extractYouTubeId(videoUrl) && <p className="text-red-400 text-xs mt-1">Not a valid YouTube link</p>}
                  </div>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-semibold text-[#EDEDED]">New Album</h3>
                    <button onClick={() => { setShowAlbumCreator(false); albumPhotos.forEach(p => URL.revokeObjectURL(p.preview)); setAlbumPhotos([]); }}
                      className="text-[#555] hover:text-[#EDEDED]"><X size={20} /></button>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Photos */}
                    <div>
                      <div className="relative border-2 border-dashed border-[#262626] hover:border-[#444] rounded-xl p-6 mb-3 transition-all cursor-pointer bg-[#0A0A0A]">
                        <input type="file" multiple accept="image/*" onChange={(e) => addAlbumFiles(Array.from(e.target.files))}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="portfolio-album-input" />
                        <div className="text-center">
                          <Camera className="w-8 h-8 mx-auto mb-2 text-[#444]" />
                          <p className="text-[#EDEDED] font-medium text-sm">{albumPhotos.length ? "Add more photos" : "Drop photos here"}</p>
                        </div>
                      </div>
                      {albumPhotos.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {albumPhotos.map((p, i) => (
                            <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-[#1A1A1A] group cursor-pointer"
                              onClick={() => setAlbumCoverIndex(i)} title="Click to set as cover">
                              <img src={p.preview} alt="" className={`w-full h-full object-cover transition-all ${i === albumCoverIndex ? "ring-2 ring-[#D4C5B0]" : "opacity-70 group-hover:opacity-100"}`} />
                              {i === albumCoverIndex && <div className="absolute bottom-1 left-1 bg-[#D4C5B0] text-[#050505] text-[9px] font-bold px-1.5 py-0.5 rounded">COVER</div>}
                              <button onClick={(e) => { e.stopPropagation(); removeAlbumPhoto(p.id); if (i < albumCoverIndex) setAlbumCoverIndex(prev => prev - 1); else if (i === albumCoverIndex) setAlbumCoverIndex(0); }}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500/80 rounded-full items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity flex">
                                <X size={10} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      {albumPhotos.length > 0 && <p className="text-[#555] text-xs mt-2">{albumPhotos.length} photo{albumPhotos.length > 1 ? "s" : ""} — click a photo to set as cover</p>}
                    </div>
                    {/* Details */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[#555] text-xs mb-1">Title *</label>
                        <input type="text" value={albumForm.title} onChange={(e) => setAlbumForm(p => ({ ...p, title: e.target.value }))}
                          placeholder="Album title" data-testid="portfolio-album-title"
                          className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[#555] text-xs mb-1">Category</label>
                          <select value={albumForm.category} onChange={(e) => setAlbumForm(p => ({ ...p, category: e.target.value }))}
                            className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm">
                            {editCategories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[#555] text-xs mb-1">Client</label>
                          <input type="text" value={albumForm.client} onChange={(e) => setAlbumForm(p => ({ ...p, client: e.target.value }))}
                            placeholder="Client name"
                            className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[#555] text-xs mb-1">Description</label>
                        <textarea value={albumForm.description} onChange={(e) => setAlbumForm(p => ({ ...p, description: e.target.value }))}
                          placeholder="Brief description" rows={2}
                          className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                      </div>
                      <details className="group">
                        <summary className="text-[#D4C5B0] text-xs uppercase tracking-widest cursor-pointer py-1">Case Study Details ▸</summary>
                        <div className="space-y-2 pt-2">
                          <textarea value={albumForm.problem} onChange={(e) => setAlbumForm(p => ({ ...p, problem: e.target.value }))}
                            placeholder="The Challenge" rows={2} className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                          <textarea value={albumForm.approach} onChange={(e) => setAlbumForm(p => ({ ...p, approach: e.target.value }))}
                            placeholder="Our Approach" rows={2} className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                          <textarea value={albumForm.outcome} onChange={(e) => setAlbumForm(p => ({ ...p, outcome: e.target.value }))}
                            placeholder="The Outcome" rows={2} className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                        </div>
                      </details>
                      <div className="flex items-center justify-between pt-2 border-t border-[#1A1A1A]">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={albumForm.featured} onChange={(e) => setAlbumForm(p => ({ ...p, featured: e.target.checked }))} className="w-4 h-4 accent-[#D4C5B0]" />
                          <span className="text-[#A3A3A3] text-sm">Featured</span>
                        </label>
                        <button onClick={saveAlbum} disabled={albumSaving || !albumPhotos.length} data-testid="save-portfolio-album-btn"
                          className="flex items-center gap-2 px-4 py-2 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-40 transition-colors">
                          {albumSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          {albumSaving ? "Saving..." : "Create Album"}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Filters */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-6 border-b border-[#1A1A1A]">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveFilter(cat)} data-testid={`filter-${cat.toLowerCase()}`}
                className={`pb-4 text-sm uppercase tracking-widest transition-all relative ${
                  activeFilter === cat ? "text-[#EDEDED]" : "text-[#555] hover:text-[#A3A3A3]"
                }`}>
                {cat}
                {activeFilter === cat && (
                  <motion.div layoutId="filter-underline" className="absolute bottom-0 left-0 right-0 h-px bg-[#D4C5B0]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Cinematic Film Strips ═══ */}
      {filteredFilms.length > 0 && (
        <section className="pb-8" data-testid="video-strips-section">
          <div className="space-y-[2px]">
            <AnimatePresence>
              {filteredFilms.map((project, index) => (
                <motion.div key={project.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}>
                  {editMode ? (
                    <div className="relative h-[180px] md:h-[220px] overflow-hidden group bg-[#0A0A0A]">
                      <img src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`}
                        className="absolute inset-0 w-full h-full object-cover opacity-60" />
                      <div className="absolute inset-0 bg-black/30" />
                      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
                        <div>
                          <h3 className="text-[#EDEDED] text-lg font-bold">{project.title}</h3>
                          <p className="text-[#A3A3A3] text-sm">{project.category}{project.client ? ` · ${project.client}` : ""}</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => toggleFeatured(project)}
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                              project.featured ? "bg-[#D4C5B0] text-[#050505]" : "bg-black/60 text-white hover:bg-[#D4C5B0] hover:text-[#050505]"
                            }`}>
                            {project.featured ? <Star size={15} /> : <StarOff size={15} />}
                          </button>
                          <button onClick={() => setEditingProject(project)}
                            className="w-9 h-9 rounded-full bg-[#D4C5B0] text-[#050505] flex items-center justify-center hover:bg-[#c4b5a0]">
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => deleteProject(project.id)}
                            className="w-9 h-9 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <VideoStrip project={project} index={index} />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </section>
      )}

      {/* ═══ Social Media Content — Vertical Portrait Cards ═══ */}
      {filteredSocial.length > 0 && (
        <section className="pb-8" data-testid="social-grid-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              <AnimatePresence>
                {filteredSocial.map((project) => {
                  const ytId = extractYouTubeId(project.video_url);
                  return (
                    <motion.div key={project.id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}>
                      {editMode ? (
                        <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl overflow-hidden group">
                          <div className="relative aspect-[9/16]">
                            <img src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`} className="w-full h-full object-cover" />
                            <div className="absolute top-2 right-2 flex gap-1.5">
                              <button onClick={() => toggleFeatured(project)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  project.featured ? "bg-[#D4C5B0] text-[#050505]" : "bg-black/60 text-white hover:bg-[#D4C5B0] hover:text-[#050505]"
                                }`}>
                                {project.featured ? <Star size={14} /> : <StarOff size={14} />}
                              </button>
                              <button onClick={() => deleteProject(project.id)}
                                className="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="p-2.5">
                            <h3 className="text-[#EDEDED] font-medium text-xs truncate">{project.title}</h3>
                          </div>
                        </div>
                      ) : (
                        <Link to={`/portfolio/${project.id}`} data-testid={`project-${project.id}`}
                          className="block relative aspect-[9/16] rounded-xl overflow-hidden group">
                          {/* Fallback thumbnail */}
                          <img src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`}
                            className="absolute inset-0 w-full h-full object-cover" />
                          {/* Autoplay loop */}
                          {ytId && (
                            <div className="absolute inset-0 z-[1] overflow-hidden">
                              <iframe
                                src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${ytId}&start=10&end=18&vq=hd1080&disablekb=1&fs=0&iv_load_policy=3`}
                                title={project.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                                style={{ width: '100%', height: '177.77%', minWidth: '100%' }}
                              />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-[2]" />
                          <div className="absolute bottom-0 left-0 right-0 p-3 z-[3]">
                            <h3 className="text-[#EDEDED] text-sm font-semibold truncate">{project.title}</h3>
                            {project.client && <p className="text-[#A3A3A3] text-xs truncate">{project.client}</p>}
                          </div>
                        </Link>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </section>
      )}

      {/* ═══ Photo Grid ═══ */}
      {filteredPhotos.length > 0 && (
        <section data-testid="photo-grid-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <AnimatePresence>
                {filteredPhotos.map((project) => (
                  <motion.div key={project.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}>
                    {editMode ? (
                      <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-lg overflow-hidden group">
                        <div className="relative aspect-[4/3]">
                          <img src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`} className="w-full h-full object-cover" />
                          {project.images?.length > 1 && (
                            <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <Images size={12} /> {project.images.length}
                            </div>
                          )}
                          <div className="absolute top-2 right-2 flex gap-1.5">
                            <button onClick={() => toggleFeatured(project)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                project.featured ? "bg-[#D4C5B0] text-[#050505]" : "bg-black/60 text-white hover:bg-[#D4C5B0] hover:text-[#050505]"
                              }`}>
                              {project.featured ? <Star size={14} /> : <StarOff size={14} />}
                            </button>
                            <button onClick={() => setEditingProject(project)} data-testid={`edit-project-${project.id}`}
                              className="w-8 h-8 rounded-full bg-[#D4C5B0] text-[#050505] flex items-center justify-center hover:bg-[#c4b5a0]">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => deleteProject(project.id)} data-testid={`delete-project-${project.id}`}
                              className="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="p-3">
                          <h3 className="text-[#EDEDED] font-medium text-sm truncate">{project.title}</h3>
                          <p className="text-[#555] text-xs">{project.category}{project.client ? ` · ${project.client}` : ""}</p>
                        </div>
                      </div>
                    ) : (
                      <Link to={`/portfolio/${project.id}`} data-testid={`project-${project.id}`}
                        className="portfolio-item block aspect-[4/3] relative group overflow-hidden">
                        <img src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`} className="w-full h-full object-cover" />
                        {project.images?.length > 1 && (
                          <div className="absolute bottom-3 left-3 z-10 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <Images size={12} /> {project.images.length}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                          <div className="flex items-end justify-between">
                            <div>
                              <span className="text-[#D4C5B0] text-xs uppercase tracking-widest mb-1 block">{project.category}</span>
                              <h3 className="text-lg font-bold text-[#EDEDED]">{project.title}</h3>
                            </div>
                            <ArrowUpRight className="text-[#D4C5B0]" size={20} />
                          </div>
                        </div>
                      </Link>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {filteredFilms.length === 0 && filteredSocial.length === 0 && filteredPhotos.length === 0 && (
        <section>
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center py-24">
            <Camera className="w-16 h-16 mx-auto mb-6 text-[#333]" />
            <p className="text-[#555] text-lg">No projects found in this category</p>
          </div>
        </section>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editingProject && (
          <EditModal project={editingProject} onClose={() => setEditingProject(null)}
            onSave={(updated) => {
              setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
              setEditingProject(null);
            }} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Edit Modal ──
function EditModal({ project, onClose, onSave }) {
  const [form, setForm] = useState({
    title: project.title || "",
    category: project.category || "Photography",
    description: project.description || "",
    client: project.client || "",
    problem: project.problem || "",
    approach: project.approach || "",
    outcome: project.outcome || "",
    featured: project.featured || false,
  });
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.title !== project.title) payload.title = form.title;
      if (form.category !== project.category) payload.category = form.category;
      if (form.description !== (project.description || "")) payload.description = form.description || null;
      if (form.client !== (project.client || "")) payload.client = form.client || null;
      if (form.problem !== (project.problem || "")) payload.problem = form.problem || null;
      if (form.approach !== (project.approach || "")) payload.approach = form.approach || null;
      if (form.outcome !== (project.outcome || "")) payload.outcome = form.outcome || null;
      if (form.featured !== project.featured) payload.featured = form.featured;

      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      await axios.patch(`${API}/portfolio/${project.id}`, payload);
      toast.success("Updated!");
      onSave({ ...project, ...form });
    } catch (e) {
      toast.error("Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80" />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} data-testid="edit-modal"
        className="relative bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        
        {/* Header with image */}
        <div className="relative aspect-[16/7] overflow-hidden rounded-t-xl">
          <img src={resolveImageUrl(project.image_url)} alt={`${project.title} – ${project.category} by OBRA Creatives, Manawatū & Wellington`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] to-transparent" />
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Title + Category */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
            <div>
              <label className="block text-[#555] text-xs mb-1.5">Title</label>
              <input type="text" value={form.title} onChange={(e) => update("title", e.target.value)}
                data-testid="edit-title"
                className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[#555] text-xs mb-1.5">Category</label>
              <select value={form.category} onChange={(e) => update("category", e.target.value)}
                data-testid="edit-category"
                className="bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm">
                {editCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Client */}
          <div>
            <label className="block text-[#555] text-xs mb-1.5">Client</label>
            <input type="text" value={form.client} onChange={(e) => update("client", e.target.value)}
              placeholder="Client name" data-testid="edit-client"
              className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[#555] text-xs mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => update("description", e.target.value)}
              placeholder="Brief description of the project" rows={2} data-testid="edit-description"
              className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
          </div>

          {/* Case Study Fields */}
          <details className="group">
            <summary className="text-[#D4C5B0] text-xs uppercase tracking-widest cursor-pointer py-2 flex items-center gap-2">
              <span>Case Study Details</span>
              <span className="text-[#555] group-open:rotate-90 transition-transform">▸</span>
            </summary>
            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-[#555] text-xs mb-1.5">The Challenge</label>
                <textarea value={form.problem} onChange={(e) => update("problem", e.target.value)}
                  placeholder="What problem did the client face?" rows={2} data-testid="edit-problem"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[#555] text-xs mb-1.5">Our Approach</label>
                <textarea value={form.approach} onChange={(e) => update("approach", e.target.value)}
                  placeholder="How did you tackle it?" rows={2} data-testid="edit-approach"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[#555] text-xs mb-1.5">The Outcome</label>
                <textarea value={form.outcome} onChange={(e) => update("outcome", e.target.value)}
                  placeholder="What was the result?" rows={2} data-testid="edit-outcome"
                  className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] px-3 py-2.5 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
              </div>
            </div>
          </details>

          {/* Featured toggle + Save */}
          <div className="flex items-center justify-between pt-2 border-t border-[#1A1A1A]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)}
                className="w-4 h-4 accent-[#D4C5B0]" />
              <span className="text-[#A3A3A3] text-sm">Featured on homepage</span>
            </label>
            <button onClick={save} disabled={saving} data-testid="save-edit-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#D4C5B0] text-[#050505] text-sm font-medium rounded-lg hover:bg-[#c4b5a0] disabled:opacity-50 transition-colors">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
